import { useEffect, useState, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Command as CommandPrimitive } from "cmdk"
import * as Dialog from "@radix-ui/react-dialog"
import { Search, FileText, FolderGit2, ArrowRight, X } from "lucide-react"
import { getAllPosts } from "@/lib/blog"
import type { BlogPostMeta } from "@/lib/types"
import { getAllProjects, type Project } from "@/lib/projects"
import { search, type SearchResult } from "@/lib/search"
import { Badge } from "@/components/ui/badge"

function highlightMatch(text: string, query: string) {
  if (!query.trim()) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-primary/20 text-foreground rounded-sm px-0.5">{part}</mark>
      : part
  );
}

export function SearchCommand() {
  const [open, setOpen] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState("")
  const navigate = useNavigate()
  const [initialData, setInitialData] = useState<{
    posts: BlogPostMeta[];
    projects: Project[];
  }>();
  const dataLoadedRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Load data only when dialog opens (lazy)
  useEffect(() => {
    if (!open || dataLoadedRef.current) return;

    const loadInitialData = async () => {
      try {
        const allPosts = getAllPosts();
        const { pinnedProjects, otherProjects } = await getAllProjects();
        setInitialData({
          posts: allPosts,
          projects: [...pinnedProjects, ...otherProjects]
        });
        dataLoadedRef.current = true;
      } catch {
        // Failed to load search data
      }
    };
    loadInitialData();
  }, [open]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // Debounced search function
  const debouncedSearch = useCallback((searchQuery: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!initialData || !searchQuery.trim()) {
      setSearchResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceTimerRef.current = setTimeout(() => {
      const results = search(searchQuery, initialData.posts, initialData.projects);
      setSearchResults(results);
      setLoading(false);
    }, 200);
  }, [initialData]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    debouncedSearch(value);
  };

  const handleSelect = (result: SearchResult) => {
    if (result.type === 'blog') {
      navigate(`/blog/${result.slug}`);
    } else {
      navigate(`/projects#${result.slug}`);
    }
    setOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open search ⌘K"
        className="inline-flex items-center gap-1.5 px-2 py-1 text-muted-foreground hover:text-foreground transition-colors rounded border border-border/40 hover:border-border/70 text-xs"
      >
        <Search className="h-3.5 w-3.5" />
        <kbd className="hidden sm:inline-flex select-none items-center font-mono text-[10px] text-muted-foreground">
          <span>⌘K</span>
        </kbd>
      </button>

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <Dialog.Content
            className="fixed z-50 top-16 sm:top-24 left-1/2 -translate-x-1/2 w-full max-w-2xl px-4 sm:px-0"
            aria-describedby={undefined}
            onPointerDownOutside={() => setOpen(false)}
            onInteractOutside={() => setOpen(false)}
          >
            <Dialog.Title className="sr-only">Search</Dialog.Title>
            <CommandPrimitive
              className="relative w-full overflow-hidden rounded-xl border bg-popover shadow-lg animate-in fade-in-0 zoom-in-95"
              shouldFilter={false}
            >
              <div className="flex items-center border-b px-3">
                <Search className="h-4 w-4 shrink-0 opacity-50" />
                <CommandPrimitive.Input
                  value={query}
                  onValueChange={handleQueryChange}
                  placeholder="Search blogs and projects..."
                  className="flex h-11 w-full rounded-md bg-transparent py-3 px-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                />
                <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 text-muted-foreground">
                  <span className="text-xs">ESC</span>
                </kbd>
                <button
                  onClick={() => setOpen(false)}
                  className="sm:hidden p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Close search"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CommandPrimitive.List className="max-h-[60vh] overflow-y-auto overscroll-contain">
                {loading ? (
                  <div className="p-6 text-center text-sm text-muted-foreground leading-relaxed">
                    Searching...
                  </div>
                ) : query.trim() === "" ? (
                  <div className="p-6 text-center text-sm text-muted-foreground leading-relaxed">
                    Start typing to search...
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground leading-relaxed">
                    No results found for "{query}"
                  </div>
                ) : (
                  <div className="px-2 py-3">
                    {searchResults.map((result) => (
                      <CommandPrimitive.Item
                        key={`${result.type}-${result.slug}`}
                        onSelect={() => handleSelect(result)}
                        onClick={() => handleSelect(result)}
                        role="button"
                        value={result.title}
                        className="group relative flex cursor-pointer select-none rounded-sm px-2 py-3 text-sm outline-none
                                 aria-selected:bg-muted aria-selected:text-foreground
                                 hover:bg-muted hover:text-foreground transition-colors
                                 data-[selected=true]:bg-muted data-[selected=true]:text-foreground"
                      >
                        <div className="flex items-center gap-2 w-full pointer-events-auto">
                          <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border/50 bg-background shrink-0 pointer-events-none">
                            {result.type === 'blog' ? (
                              <FileText className="h-5 w-5 text-primary" />
                            ) : (
                              <FolderGit2 className="h-5 w-5 text-primary" />
                            )}
                          </div>

                          <div className="flex-1 space-y-1 pointer-events-none">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{highlightMatch(result.title, query)}</span>
                              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-muted-foreground/70 transition-colors ml-auto" />
                            </div>
                            <div className="line-clamp-1 text-muted-foreground text-xs">
                              {highlightMatch(result.description, query)}
                            </div>
                            {result.contentMatch && result.contentMatch.length > 0 && (
                              <div className="mt-1 space-y-1">
                                {result.contentMatch.map((match, idx) => (
                                  <div key={idx} className="text-xs bg-muted/50 rounded p-1">
                                    {highlightMatch(match.text, query)}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              {result.tags.slice(0, 3).map(tag => (
                                <Badge
                                  key={tag}
                                  variant="secondary"
                                  className="text-[10px] px-1 py-0 rounded"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CommandPrimitive.Item>
                    ))}
                  </div>
                )}
              </CommandPrimitive.List>
            </CommandPrimitive>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
